/**
 * Feature-access helpers — single place to gate features behind a plan.
 *
 * The Plans page already lists "Speed drills & timed challenges" and "Custom
 * word lists" as Plus/Pro-only, but for launch we're opening Challenges to
 * everyone so users can try the surface. When billing is wired up, flip the
 * defaults below (or read from the user's plan) and every check site-wide
 * will switch in lockstep.
 *
 * Keep this module dependency-free so it can be imported from server
 * components, client components, and the legacy app code alike.
 */

export type Plan = "free" | "plus" | "pro";

export interface FeatureAccess {
    /** Browse and run preset speed/timed drills. */
    challenges: boolean;
    /** Build a custom drill from your own word list. */
    customWordLists: boolean;
}

/**
 * Map a plan to the feature gates it unlocks.
 *
 * Today every gate returns `true` regardless of plan — that's the launch
 * behavior. To paywall later, change the `free` row to the real defaults
 * (e.g. `challenges: false, customWordLists: false`).
 */
export function accessForPlan(_plan: Plan | null | undefined): FeatureAccess {
    // Launch posture: everything open. Keep the parameter so callers don't
    // have to change when we re-tighten this.
    return {
        challenges: true,
        customWordLists: true,
    };
}

const PLAN_RANK: Record<Plan, number> = { free: 0, plus: 1, pro: 2 };

/**
 * Resolve the plan a user should actually be served, folding in a referral
 * reward ("invite 5 friends, get a month of Pro") and school membership
 * (students and supervisors are served Plus). The bonus is stored as
 * `users.referral_bonus_until` rather than by writing `plan`, so that Stripe's
 * webhooks — which own the plan/subscription columns — can never clobber an
 * earned month, and an earned month can never be mistaken for a paid
 * subscription.
 *
 * Callers should gate on `accessForPlan(effectivePlan(user.plan, user.referral_bonus_until, isSchoolMember))`.
 * While every gate is open this changes nothing, but it's the seam that makes
 * the referral reward real the moment the gates below are tightened.
 */
export function effectivePlan(
    plan: Plan | null | undefined,
    referralBonusUntil: string | Date | null | undefined,
    schoolMember = false
): Plan {
    const paid: Plan = plan ?? "free";
    const base: Plan = schoolMember && PLAN_RANK[paid] < PLAN_RANK.plus ? "plus" : paid;
    if (!referralBonusUntil) return base;
    const until = new Date(referralBonusUntil);
    if (Number.isNaN(until.getTime()) || until.getTime() <= Date.now()) {
        return base;
    }
    // Never downgrade someone who already pays for something at or above Pro.
    return PLAN_RANK[base] >= PLAN_RANK.pro ? base : "pro";
}

/**
 * Convenience for the Challenges surface — returns `true` if the visitor
 * has access at all (used to decide whether to render the SideNav entry,
 * preset list, etc).
 */
export function canUseChallenges(plan: Plan | null | undefined): boolean {
    return accessForPlan(plan).challenges;
}

/**
 * Convenience for the custom-drill builder.
 */
export function canUseCustomWordLists(plan: Plan | null | undefined): boolean {
    return accessForPlan(plan).customWordLists;
}
